// ScanDetailsTab.js - Asset Scan Details Tab Component
// Displays tool summary cards and raw JSON data for asset scans

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getAssetScanDetails, getAssetRawDataUrl } from '../../services/assetService';
import { useThemeColors } from '../../hooks/useThemeColors';

const ScanDetailsTab = ({ assetId, history, asset }) => {
    const themeColors = useThemeColors();

    // Color scheme - theme-aware via useThemeColors hook
    const COLORS = {
        brandRed: themeColors.brand,
        neonBlue: themeColors.accent.primary,
        neonGreen: themeColors.risk.healthy,
        textPrimary: themeColors.text.primary,
        textMuted: themeColors.text.muted,
        bgCard: themeColors.bg.card,
        bgSecondary: themeColors.bg.secondary,
    };
    const [selectedResultId, setSelectedResultId] = useState(null);
    const [scanDetails, setScanDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        stageCards: true,
        rawToolData: false,
        fullJson: false
    });

    // Get list of recent scans from history (memoized to avoid useEffect dependency issues)
    const recentScans = useMemo(() => {
        return history?.scans?.slice(0, 5) || [];
    }, [history?.scans]);

    // Load scan details when selection changes
    const loadScanDetails = useCallback(async (resultId) => {
        if (!resultId || !assetId) return;

        setLoading(true);
        setError(null);

        try {
            const data = await getAssetScanDetails(assetId, resultId);
            setScanDetails(data);
        } catch (err) {
            setError(err.message || 'Failed to load scan details');
            setScanDetails(null);
        } finally {
            setLoading(false);
        }
    }, [assetId]);

    // Auto-select first scan on mount
    useEffect(() => {
        if (recentScans.length > 0 && !selectedResultId) {
            const firstScan = recentScans[0];
            setSelectedResultId(firstScan.resultId);
        }
    }, [recentScans, selectedResultId]);

    // Load details when selection changes
    useEffect(() => {
        if (selectedResultId) {
            loadScanDetails(selectedResultId);
        }
    }, [selectedResultId, loadScanDetails]);

    const handleScanSelect = (e) => {
        setSelectedResultId(e.target.value);
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    const handleDownloadRawData = async () => {
        if (!selectedResultId) return;

        setDownloadLoading(true);
        try {
            const response = await getAssetRawDataUrl(assetId, selectedResultId);
            if (response.url) {
                window.open(response.url, '_blank');
            }
        } catch (err) {
            setError(err.message || 'Failed to get download URL');
        } finally {
            setDownloadLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // If no scans available
    if (recentScans.length === 0) {
        return (
            <div className="scan-details-empty">
                <p>No scan results available yet. Run a baseline scan to see details.</p>
            </div>
        );
    }

    // Extract stage data from fullResult
    const fullResult = scanDetails?.fullResult || {};
    const stageResults = fullResult.stageResults || {};
    const discoveryData = fullResult.discoveryData || {}; // From vuln-scan merged inventory

    // Stage 1: DNS Enumeration - check both stageResults and discoveryData
    const dnsStage = stageResults.dnsEnumeration || {};
    const dnsMetrics = dnsStage.metrics || {
        subdomainCount: discoveryData.subdomains?.length || 0,
        liveSubdomainCount: discoveryData.liveSubdomains?.length || 0,
        dnsRecordCount: 0,
        ipv4Count: discoveryData.ips?.length || 0
    };

    // Stage 2: IP Resolution
    const ipStage = stageResults.ipResolution || {};
    const ipMetrics = ipStage.metrics || {
        totalIps: discoveryData.ips?.length || 0,
        cdnIps: 0,
        cloudIps: 0,
        uniqueAsns: 0
    };

    // Stage 3: Port Scanning
    const portStage = stageResults.portScanning || {};
    const portMetrics = portStage.metrics || {
        openPortCount: discoveryData.ports?.length || 0,
        hostsWithOpenPorts: 0,
        servicesIdentified: discoveryData.ports?.length || 0,
        highRiskServices: 0
    };

    // Stage 4: Web Crawling
    const webStage = stageResults.webCrawling || {};
    const webMetrics = webStage.metrics || {
        totalUrls: discoveryData.urls?.length || 0,
        liveWebServers: discoveryData.urls?.length || 0,
        technologiesDetected: discoveryData.technologies?.length || 0
    };

    // Stage 5: Vulnerability Scanning - check both stageResults and findings
    const vulnStage = stageResults.vulnScanning || {};
    const findingsArray = fullResult.findings || vulnStage.findings || [];
    const vulnMetrics = vulnStage.metrics || fullResult.vulnerabilities || {
        totalFindings: findingsArray.length,
        criticalCount: findingsArray.filter(f => (f.severity || '').toLowerCase() === 'critical').length,
        highCount: findingsArray.filter(f => (f.severity || '').toLowerCase() === 'high').length,
        mediumCount: findingsArray.filter(f => (f.severity || '').toLowerCase() === 'medium').length,
        lowCount: findingsArray.filter(f => (f.severity || '').toLowerCase() === 'low').length,
        infoCount: findingsArray.filter(f => (f.severity || '').toLowerCase() === 'info').length
    };

    // Raw data sections for JSON display
    // Include both legacy stageResults and new vuln-scan data structures
    const rawDataSections = [];

    // Add discoveryData if available (from vuln-scan merged inventory)
    if (Object.keys(discoveryData).length > 0) {
        rawDataSections.push({
            name: 'Discovery Data',
            key: 'discoveryData',
            data: {
                subdomains: discoveryData.subdomains?.length || 0,
                liveSubdomains: discoveryData.liveSubdomains || [],
                ips: discoveryData.ips || [],
                ports: discoveryData.ports || [],
                urls: (discoveryData.urls || []).slice(0, 20),
                technologies: discoveryData.technologies || [],
                wafDetected: discoveryData.wafDetected,
                wafType: discoveryData.wafType,
                lastDiscoveryAt: discoveryData.lastDiscoveryAt
            }
        });
    }

    // Add findings summary if available
    if (findingsArray.length > 0) {
        rawDataSections.push({
            name: `Findings (${findingsArray.length} total)`,
            key: 'findings',
            data: {
                totalCount: findingsArray.length,
                bySeverity: {
                    critical: findingsArray.filter(f => (f.severity || '').toLowerCase() === 'critical').length,
                    high: findingsArray.filter(f => (f.severity || '').toLowerCase() === 'high').length,
                    medium: findingsArray.filter(f => (f.severity || '').toLowerCase() === 'medium').length,
                    low: findingsArray.filter(f => (f.severity || '').toLowerCase() === 'low').length,
                    info: findingsArray.filter(f => (f.severity || '').toLowerCase() === 'info').length
                },
                sample: findingsArray.slice(0, 10).map(f => ({
                    name: f.name,
                    severity: f.severity,
                    host: f.host,
                    source: f.source
                }))
            }
        });
    }

    // Add SSL analysis if available
    if (fullResult.sslAnalysis) {
        rawDataSections.push({ name: 'SSL Analysis', key: 'sslAnalysis', data: fullResult.sslAnalysis });
    }

    // Add SSH audit if available
    if (fullResult.sshAudit) {
        rawDataSections.push({ name: 'SSH Audit', key: 'sshAudit', data: fullResult.sshAudit });
    }

    // Add legacy stageResults if they have actual data
    if (Object.keys(dnsStage).length > 0) {
        rawDataSections.push({ name: 'DNS Enumeration', key: 'dnsEnumeration', data: dnsStage });
    }
    if (Object.keys(ipStage).length > 0) {
        rawDataSections.push({ name: 'IP Resolution', key: 'ipResolution', data: ipStage });
    }
    if (Object.keys(portStage).length > 0) {
        rawDataSections.push({ name: 'Port Scanning', key: 'portScanning', data: portStage });
    }
    if (Object.keys(webStage).length > 0) {
        rawDataSections.push({ name: 'Web Crawling', key: 'webCrawling', data: webStage });
    }
    if (Object.keys(vulnStage).length > 0) {
        rawDataSections.push({ name: 'Vulnerability Scanning', key: 'vulnScanning', data: vulnStage });
    }

    // Add legacy summary objects if they have data
    if (fullResult.discovery && Object.keys(fullResult.discovery).length > 0) {
        rawDataSections.push({ name: 'Discovery Summary', key: 'discovery', data: fullResult.discovery });
    }
    if (fullResult.services && Object.keys(fullResult.services).length > 0) {
        rawDataSections.push({ name: 'Services Summary', key: 'services', data: fullResult.services });
    }
    if (fullResult.webApps && Object.keys(fullResult.webApps).length > 0) {
        rawDataSections.push({ name: 'Web Apps Summary', key: 'webApps', data: fullResult.webApps });
    }
    if (fullResult.vulnerabilities && Object.keys(fullResult.vulnerabilities).length > 0) {
        rawDataSections.push({ name: 'Vulnerabilities Summary', key: 'vulnerabilities', data: fullResult.vulnerabilities });
    }
    if (fullResult.threatIntel && Object.keys(fullResult.threatIntel).length > 0) {
        rawDataSections.push({ name: 'Threat Intelligence', key: 'threatIntel', data: fullResult.threatIntel });
    }

    // PRD-56 §5.2B: append "(WAF-limited)" suffix to scan-selector option labels
    // when the asset is currently WAF-blocked. v1 uses the asset-level signal,
    // not per-scan — every option gets the suffix if the asset is blocked.
    // Stability layer: prefer Display fields; require sample >= 10.
    const wafFraction = typeof asset?.wafBlockingDisplayFraction === 'number'
        ? asset.wafBlockingDisplayFraction
        : (typeof asset?.wafBlockingFraction === 'number' ? asset.wafBlockingFraction : 0);
    const wafSampleSize = asset?.wafBlockingSampleSize || 0;
    const wafLimited = wafFraction > 0.5 && wafSampleSize >= 10;

    return (
        <div className="scan-details-tab">
            {/* Scan Selector */}
            <div className="scan-selector">
                <label htmlFor="scan-select">Select Scan Result:</label>
                <select
                    id="scan-select"
                    value={selectedResultId || ''}
                    onChange={handleScanSelect}
                >
                    {recentScans.map((scan) => (
                        <option key={scan.resultId} value={scan.resultId}>
                            {scan.scanType?.toUpperCase()} - {formatDate(scan.completedAt)} - {scan.riskLevel || 'Unknown'}{wafLimited ? ' (WAF-limited)' : ''}
                        </option>
                    ))}
                </select>

                {/* Download Button */}
                {scanDetails?.rawDataZipPath && (
                    <button
                        className="btn-download-rawdata"
                        onClick={handleDownloadRawData}
                        disabled={downloadLoading}
                    >
                        {downloadLoading ? 'Getting URL...' : 'Download Raw Data (ZIP)'}
                    </button>
                )}
            </div>

            {/* Error Message */}
            {error && (
                <div className="scan-details-error">
                    <span className="error-icon">!</span>
                    {error}
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className="scan-details-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading scan details...</p>
                </div>
            )}

            {/* Scan Details Content */}
            {!loading && scanDetails && (
                <>
                    {/* Scan Info Header */}
                    <div className="scan-info-header">
                        <div className="scan-info-item">
                            <span className="info-label">Scan Type</span>
                            <span className="info-value">{scanDetails.scanType?.toUpperCase()}</span>
                        </div>
                        <div className="scan-info-item">
                            <span className="info-label">Completed</span>
                            <span className="info-value">{formatDate(scanDetails.completedAt)}</span>
                        </div>
                        <div className="scan-info-item">
                            <span className="info-label">Risk Level</span>
                            <span className={`info-value risk-${scanDetails.riskLevel?.toLowerCase()}`}>
                                {scanDetails.riskLevel}
                            </span>
                        </div>
                    </div>

                    {/* Stage Summary Cards */}
                    <div className="section-container">
                        <div
                            className="section-header"
                            onClick={() => toggleSection('stageCards')}
                        >
                            <h3>Stage Summary Cards</h3>
                            <span className="toggle-icon">{expandedSections.stageCards ? '[-]' : '[+]'}</span>
                        </div>

                        {expandedSections.stageCards && (
                            <div className="stage-cards-grid">
                                {/* Stage 1: DNS Enumeration */}
                                <div className="stage-card" style={{ borderTopColor: COLORS.neonBlue }}>
                                    <div className="stage-card-header">
                                        <span className="stage-icon">1</span>
                                        <h4>DNS Enumeration</h4>
                                    </div>
                                    <div className="stage-card-content">
                                        <div className="stage-metric">
                                            <span className="stage-metric-label">Subdomains Found</span>
                                            <span className="stage-metric-value">{dnsMetrics.subdomainCount || 0}</span>
                                        </div>
                                        <div className="stage-metric">
                                            <span className="stage-metric-label">Live Subdomains</span>
                                            <span className="stage-metric-value">{dnsMetrics.liveSubdomainCount || 0}</span>
                                        </div>
                                        <div className="stage-metric">
                                            <span className="stage-metric-label">DNS Records</span>
                                            <span className="stage-metric-value">{dnsMetrics.dnsRecordCount || 0}</span>
                                        </div>
                                        <div className="stage-metric">
                                            <span className="stage-metric-label">IPv4 Addresses</span>
                                            <span className="stage-metric-value">{dnsMetrics.ipv4Count || 0}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Stage 2: IP Resolution */}
                                <div className="stage-card" style={{ borderTopColor: COLORS.neonBlue }}>
                                    <div className="stage-card-header">
                                        <span className="stage-icon">2</span>
                                        <h4>IP Resolution</h4>
                                    </div>
                                    <div className="stage-card-content">
                                        <div className="stage-metric">
                                            <span className="stage-metric-label">Total IPs</span>
                                            <span className="stage-metric-value">{ipMetrics.totalIps || 0}</span>
                                        </div>
                                        <div className="stage-metric">
                                            <span className="stage-metric-label">CDN IPs</span>
                                            <span className="stage-metric-value">{ipMetrics.cdnIps || 0}</span>
                                        </div>
                                        <div className="stage-metric">
                                            <span className="stage-metric-label">Cloud IPs</span>
                                            <span className="stage-metric-value">{ipMetrics.cloudIps || 0}</span>
                                        </div>
                                        <div className="stage-metric">
                                            <span className="stage-metric-label">Unique ASNs</span>
                                            <span className="stage-metric-value">{ipMetrics.uniqueAsns || 0}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Stage 3: Port Scanning */}
                                <div className="stage-card" style={{ borderTopColor: COLORS.neonBlue }}>
                                    <div className="stage-card-header">
                                        <span className="stage-icon">3</span>
                                        <h4>Port Scanning</h4>
                                    </div>
                                    <div className="stage-card-content">
                                        <div className="stage-metric">
                                            <span className="stage-metric-label">Open Ports</span>
                                            <span className="stage-metric-value">{portMetrics.openPortCount || 0}</span>
                                        </div>
                                        <div className="stage-metric">
                                            <span className="stage-metric-label">Hosts with Open Ports</span>
                                            <span className="stage-metric-value">{portMetrics.hostsWithOpenPorts || 0}</span>
                                        </div>
                                        <div className="stage-metric">
                                            <span className="stage-metric-label">Services Identified</span>
                                            <span className="stage-metric-value">{portMetrics.servicesIdentified || 0}</span>
                                        </div>
                                        <div className="stage-metric">
                                            <span className="stage-metric-label">High Risk Services</span>
                                            <span className="stage-metric-value" style={{ color: portMetrics.highRiskServices > 0 ? COLORS.brandRed : 'inherit' }}>
                                                {portMetrics.highRiskServices || 0}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Stage 4: Web Crawling */}
                                <div className="stage-card" style={{ borderTopColor: COLORS.neonBlue }}>
                                    <div className="stage-card-header">
                                        <span className="stage-icon">4</span>
                                        <h4>Web Crawling</h4>
                                    </div>
                                    <div className="stage-card-content">
                                        <div className="stage-metric">
                                            <span className="stage-metric-label">Live Web Servers</span>
                                            <span className="stage-metric-value">
                                                {webMetrics.liveWebServers || webStage.webServers?.length || webStage.liveUrls?.length || fullResult.webApps?.liveWebServers || 0}
                                            </span>
                                        </div>
                                        <div className="stage-metric">
                                            <span className="stage-metric-label">Technologies</span>
                                            <span className="stage-metric-value">
                                                {webMetrics.uniqueTechnologies || webStage.technologies?.length || fullResult.webApps?.technologies || 0}
                                            </span>
                                        </div>
                                        <div className="stage-metric">
                                            <span className="stage-metric-label">Sites with WAF</span>
                                            <span className="stage-metric-value">
                                                {webMetrics.sitesWithWaf || fullResult.webApps?.sitesWithWaf || 0}
                                            </span>
                                        </div>
                                        <div className="stage-metric">
                                            <span className="stage-metric-label">URLs Crawled</span>
                                            <span className="stage-metric-value">
                                                {webMetrics.urlsCrawled || webStage.crawledUrls?.length || fullResult.webApps?.urlsCrawled || 0}
                                            </span>
                                        </div>
                                        {/* Show additional URL categories if available */}
                                        {(webStage.dynamicUrls?.length > 0 || webMetrics.dynamicUrls > 0) && (
                                            <div className="stage-metric">
                                                <span className="stage-metric-label">Dynamic URLs</span>
                                                <span className="stage-metric-value">
                                                    {webMetrics.dynamicUrls || webStage.dynamicUrls?.length || 0}
                                                </span>
                                            </div>
                                        )}
                                        {(webStage.apiUrls?.length > 0 || webMetrics.apiUrls > 0) && (
                                            <div className="stage-metric">
                                                <span className="stage-metric-label">API Endpoints</span>
                                                <span className="stage-metric-value" style={{ color: COLORS.neonGreen }}>
                                                    {webMetrics.apiUrls || webStage.apiUrls?.length || 0}
                                                </span>
                                            </div>
                                        )}
                                        {(webStage.interestingUrls?.length > 0 || webMetrics.interestingUrls > 0) && (
                                            <div className="stage-metric">
                                                <span className="stage-metric-label">Interesting URLs</span>
                                                <span className="stage-metric-value" style={{ color: COLORS.neonGreen }}>
                                                    {webMetrics.interestingUrls || webStage.interestingUrls?.length || 0}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Stage 5: Vulnerability Scanning */}
                                <div className="stage-card" style={{ borderTopColor: COLORS.brandRed }}>
                                    <div className="stage-card-header">
                                        <span className="stage-icon">5</span>
                                        <h4>Vulnerability Scanning</h4>
                                    </div>
                                    <div className="stage-card-content">
                                        <div className="stage-metric">
                                            <span className="stage-metric-label">Total Findings</span>
                                            <span className="stage-metric-value">{vulnMetrics.total || vulnMetrics.totalFindings || 0}</span>
                                        </div>
                                        <div className="stage-metric">
                                            <span className="stage-metric-label">Critical</span>
                                            <span className="stage-metric-value" style={{ color: COLORS.brandRed }}>
                                                {vulnMetrics.critical || 0}
                                            </span>
                                        </div>
                                        <div className="stage-metric">
                                            <span className="stage-metric-label">High</span>
                                            <span className="stage-metric-value" style={{ color: '#ff6b35' }}>
                                                {vulnMetrics.high || 0}
                                            </span>
                                        </div>
                                        <div className="stage-metric">
                                            <span className="stage-metric-label">CISA KEV Matches</span>
                                            <span className="stage-metric-value" style={{ color: vulnMetrics.cisaKevMatches > 0 ? COLORS.brandRed : 'inherit' }}>
                                                {vulnMetrics.cisaKevMatches || 0}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Raw Tool Data */}
                    <div className="section-container">
                        <div
                            className="section-header"
                            onClick={() => toggleSection('rawToolData')}
                        >
                            <h3>Raw Tool Data (JSON)</h3>
                            <span className="toggle-icon">{expandedSections.rawToolData ? '[-]' : '[+]'}</span>
                        </div>

                        {expandedSections.rawToolData && (
                            <div className="raw-tool-data-grid">
                                {rawDataSections.length > 0 ? (
                                    rawDataSections.map((section) => (
                                        <div key={section.key} className="raw-tool-card">
                                            <div className="raw-tool-header">
                                                <span className="raw-tool-name">{section.name}</span>
                                                <button
                                                    className="btn-copy"
                                                    onClick={() => copyToClipboard(JSON.stringify(section.data, null, 2))}
                                                >
                                                    Copy
                                                </button>
                                            </div>
                                            <div className="raw-tool-content">
                                                <pre>{JSON.stringify(section.data, null, 2)}</pre>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="no-data-message">
                                        No raw tool data available for this scan.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                </>
            )}
        </div>
    );
};

export default ScanDetailsTab;
